import axios from 'axios';
import { User, UserRole } from '@timesheet/schemas';
import { getApiBaseUrl } from '../lib/api-base-url';

const API_URL = getApiBaseUrl();

export type ApiUser = User & {
  codiceFiscale?: string | null;
  approver?: ApproverCandidate | null;
};

/** A user that can be picked as somebody's direct approver (active SUPER_ADMIN/ADMIN/MANAGER). */
export interface ApproverCandidate {
  id: number;
  name: string;
  email: string;
  role: UserRole;
}

export interface UserImportPreviewRow {
  rowNumber: number;
  valid: boolean;
  error?: string;
  name?: string;
  email?: string;
  role?: string;
  tenantName?: string;
  supplierName?: string;
}

export interface UserImportPreviewResult {
  total: number;
  created: number;
  valid: number;
  errors: string[];
  rows: UserImportPreviewRow[];
  unresolvedTenants: string[];
  unresolvedSuppliers: string[];
}

export const userService = {
  /** Users of the caller's tenant (MANAGER+ only: OPERATOR gets 401 and is logged out). */
  getAll: async (options?: { includeInactive?: boolean }): Promise<ApiUser[]> => {
    const response = await axios.get(`${API_URL}/users`, {
      params: options?.includeInactive ? { includeInactive: '1' } : undefined,
    });
    return response.data;
  },

  getById: async (id: number): Promise<ApiUser> => {
    const response = await axios.get(`${API_URL}/users/${id}`);
    return response.data;
  },

  /** People whose direct approver is the logged-in user: the "my approvals" scope. */
  getMyDirectReports: async (): Promise<ApproverCandidate[]> => {
    const response = await axios.get(`${API_URL}/users/me/direct-reports`);
    return response.data;
  },

  /** Eligible approvers of the caller's tenant; Super Admins may pass another tenant id. */
  getApproverCandidates: async (tenantId?: number): Promise<ApproverCandidate[]> => {
    const response = await axios.get(`${API_URL}/users/approvers`, {
      params: tenantId ? { tenantId } : undefined,
    });
    return response.data;
  },

  previewImport: async (
    file: File,
    tenantMappings: Record<string, number> = {},
    supplierMappings: Record<string, number> = {},
  ): Promise<UserImportPreviewResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tenantMappings', JSON.stringify(tenantMappings));
    formData.append('supplierMappings', JSON.stringify(supplierMappings));
    const response = await axios.post(`${API_URL}/users/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  import: async (
    file: File,
    tenantMappings: Record<string, number> = {},
    supplierMappings: Record<string, number> = {},
  ): Promise<UserImportPreviewResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('tenantMappings', JSON.stringify(tenantMappings));
    formData.append('supplierMappings', JSON.stringify(supplierMappings));
    const response = await axios.post(`${API_URL}/users/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  downloadImportTemplate: async (): Promise<Blob> => {
    const response = await axios.get(`${API_URL}/users/import/template`, {
      responseType: 'arraybuffer',
    });
    return new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  },
};
